import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { PageHero, FinalCta } from "../components/Sections";
import { MARKETS } from "../data/site";
import { METROS } from "../data/metros";

const Markets = () => {
    useEffect(() => {
        document.title = "Markets We Serve | Narmadapuram, Itarsi & Central MP — ALBL Communications";
    }, []);

    return (
        <main>
            <PageHero
                eyebrow="Markets"
                title="STARTING LOCAL. THINKING BIG."
                sub="ALBL works with businesses across Narmadapuram, Itarsi and surrounding Central Madhya Pradesh markets."
            />

            <section className="bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <Reveal>
                        <Eyebrow>Primary markets</Eyebrow>
                    </Reveal>
                    <div className="mt-10 grid md:grid-cols-2 gap-6">
                        {MARKETS.filter((m) => m.primary).map((m, i) => (
                            <Reveal key={m.name} delay={i * 80}>
                                <div
                                    className="border border-slate-200 bg-[#F8FAFC] p-8 sm:p-12"
                                    data-testid={`market-card-${m.name.toLowerCase()}`}
                                >
                                    <MapPin className="h-6 w-6 text-[#C5A059]" />
                                    <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-4">
                                        {m.name}
                                    </h2>
                                    <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                                        {m.name === "Narmadapuram"
                                            ? "Our home market. Brand strategy, retail and trade marketing, digital growth, customer acquisition and sales-focused marketing for businesses across Narmadapuram."
                                            : "A commercial crossroads of Central MP. We help Itarsi businesses turn location and trading strength into structured, measurable growth."}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={100}>
                        <Eyebrow>
                            <span className="block mt-20">Metro markets</span>
                        </Eyebrow>
                        <div className="mt-8 grid sm:grid-cols-3 gap-4">
                            {METROS.map((m) => (
                                <Link
                                    key={m.slug}
                                    to={`/markets/${m.slug}`}
                                    data-testid={`market-metro-card-${m.slug}`}
                                    className="group border border-slate-200 bg-white p-7 hover:border-[#0D131F] transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-display text-xl font-bold text-slate-900 group-hover:text-[#1E3A8A] transition-colors">
                                            {m.city}
                                        </h3>
                                        <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#C5A059] group-hover:translate-x-1 transition-all" />
                                    </div>
                                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{m.heroSub}</p>
                                </Link>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal delay={150}>
                        <Eyebrow>
                            <span className="block mt-20">Service areas</span>
                        </Eyebrow>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {MARKETS.filter((m) => !m.primary && !METROS.some((x) => x.city === m.name)).map((m) => (
                                <span
                                    key={m.name}
                                    className="px-5 py-3 text-sm tracking-wide border border-slate-300 text-slate-700"
                                    data-testid={`market-area-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {m.name}
                                </span>
                            ))}
                        </div>
                        <p className="mt-10 text-slate-600 text-base sm:text-lg leading-relaxed max-w-3xl">
                            Whether your business serves one neighbourhood or distributes across the
                            region, we build marketing around the geography you actually sell in —
                            measured in enquiries, customers and sales.
                        </p>
                        <Link
                            to="/contact?intent=audit"
                            data-testid="markets-cta-audit-button"
                            className="btn-sharp mt-10 inline-flex items-center gap-2 bg-[#0D131F] text-white text-xs font-bold tracking-[0.15em] uppercase px-8 py-4"
                        >
                            Get a Growth Audit <ArrowRight className="h-4 w-4 text-[#C5A059]" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <FinalCta />
        </main>
    );
};

export default Markets;
